import {
	ActionListDivider,
	ActionListHeader,
	ActionListItem,
	Toolbar
} from "client/Components";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useAlerts } from "client/Hooks";
import {
	AddTilesOnSpaceInformation,
	AddTileTargetKind,
	getAddTilesOnSpaceInformation,
	SpaceKind,
	TileGroupType,
	TileGroupListItemModel
} from "client/Api/Workspace/workspace";
import { AddTilesModel } from "../AddTiles";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { SpaceListItem } from "client/Api/Apps/AppStore/appStore";

export type AddTilesButtonProps = {
	targetTileGroupId: number | null;
	targetSpace: SpaceListItem | null;
	isDisabled?: boolean;
	onClick: (target: AddTilesModel) => void;
};

export const AddTilesButton = (props: AddTilesButtonProps): ReactElement | null => {
	const {
		targetTileGroupId,
		targetSpace,
		isDisabled = false,
		onClick,
	} = props;

	const [tileGroups, setTileGroups] = useState<TileGroupListItemModel[]>([]);
	const [creatableTileGroupKinds, setCreatableTileGroupKinds] = useState<TileGroupType[]>([]);
	const [tileSpaceId, setTileSpaceId] = useState<number | null>(null);
	const { showError } = useAlerts();
	const isInSharedSpace = targetSpace?.kind === SpaceKind.Shared;

	const preSelectedGroup: TileGroupListItemModel | undefined = tileGroups.find(
		(group) => group.id === targetTileGroupId
	);

	const sortedTileGroups: TileGroupListItemModel[] = useMemo(
		() => tileGroups.sort((a, b) => a.name.localeCompare(b.name)),
		[tileGroups]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();

			const getInfo = async (): Promise<void> => {
				if (!targetSpace) {
					return;
				}

				try {
					const response: AddTilesOnSpaceInformation = await getAddTilesOnSpaceInformation(
						targetSpace.id,
						cancellation.signal
					);

					setTileGroups([...response.tileGroups]);
					setCreatableTileGroupKinds([...response.creatableTileGroupKinds]);
					setTileSpaceId(response.spaceId);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					showError(localize("_GeneralErrorMessage"));
				}
			};

			getInfo();

			return () => {
				cancellation.abort();
			};
		},
		[showError, targetSpace]
	);

	const handleGroupClick = (groupId: number) => (): void => {
		const target: AddTilesModel = {
			kind: AddTileTargetKind.ExistingTileGroup,
			tileGroupId: groupId,
			spaceId: tileSpaceId,
		};

		onClick(target);
	};

	const canCreateSharedTileGroup: boolean = creatableTileGroupKinds.includes(TileGroupType.Shared);
	const canCreatePersonalTileGroup: boolean = creatableTileGroupKinds.includes(TileGroupType.Personal);

	const newPersonalGroupTarget = useMemo(
		(): AddTilesModel | null => {
			return !canCreatePersonalTileGroup || isNil(tileSpaceId)
				? null
				: {
					kind: AddTileTargetKind.NewTileGroup,
					tileGroupKind: TileGroupType.Personal,
					spaceId: tileSpaceId,
				};
		},
		[canCreatePersonalTileGroup, tileSpaceId]
	);

	const newSharedGroupTarget = useMemo(
		(): AddTilesModel | null => {
			return !canCreateSharedTileGroup || isNil(tileSpaceId)
				? null
				: {
					kind: AddTileTargetKind.NewTileGroup,
					tileGroupKind: TileGroupType.Shared,
					spaceId: tileSpaceId
				};
		},
		[canCreateSharedTileGroup, tileSpaceId]
	);

	const mainButtonTarget = useMemo(
		(): AddTilesModel | null => {
			if (preSelectedGroup) {
				return {
					kind: AddTileTargetKind.ExistingTileGroup,
					tileGroupId: preSelectedGroup.id,
					spaceId: tileSpaceId,
				};
			}

			if (isInSharedSpace && isNotNil(newSharedGroupTarget)) {
				return newSharedGroupTarget;
			}

			if (newPersonalGroupTarget) {
				return newPersonalGroupTarget;
			}

			if (tileGroups.length > 0) {
				const [firstGroup] = tileGroups;
				return {
					kind: AddTileTargetKind.ExistingTileGroup,
					tileGroupId: firstGroup.id,
					spaceId: tileSpaceId,
				};
			}

			return null;
		},
		[
			isInSharedSpace,
			newPersonalGroupTarget,
			newSharedGroupTarget,
			preSelectedGroup,
			tileGroups,
			tileSpaceId
		]
	);

	const getTitleForTarget = useCallback(
		(target: AddTilesModel): string => {
			switch (target.kind) {
				case AddTileTargetKind.NewTileGroup:
					switch (target.tileGroupKind) {
						case TileGroupType.Personal:
							return localize("_AddToNewPersonalGroup");
						case TileGroupType.Shared:
							return localize("_AddToNewSharedGroup");
						default:
							return never(target.tileGroupKind);
					}
				case AddTileTargetKind.ExistingTileGroup:
					const tileGroup: TileGroupListItemModel | undefined = tileGroups
						.find(g => g.id === target.tileGroupId);

					const tileGroupName: string = ensureNotNil(tileGroup).name;
					return localize("_AddToGroupWithGroupName")
						.replace(
							"{GroupName}",
							tileGroupName
						);
				default:
					return never(target);
			}
		},
		[tileGroups]
	);

	const showDivider: boolean = sortedTileGroups.length > 0;

	return (
		mainButtonTarget && (
			<Toolbar.SplitButton
				kind="primary"
				onClick={() => onClick(mainButtonTarget)}
				text={getTitleForTarget(mainButtonTarget)}
				menuTitle={localize("_AddToGroup")}
				isDisabled={isDisabled}
				responsiveBehavior="always-visible"
				trackingId="add-tiles"
			>
				{newPersonalGroupTarget && (
					<ActionListItem
						text={getTitleForTarget(newPersonalGroupTarget)}
						icon="plus"
						onClick={() =>
							onClick(newPersonalGroupTarget)
						}
					/>
				)}
				{newSharedGroupTarget && (
					<ActionListItem
						icon="plus"
						text={getTitleForTarget(newSharedGroupTarget)}
						onClick={() => onClick(newSharedGroupTarget)}
					/>
				)}
				{showDivider && <ActionListDivider />}
				{sortedTileGroups.length > 0 && (
					<ActionListHeader
						text={localize("_ChooseExistingGroup")}
					/>
				)}
				{sortedTileGroups
					.map((tileGroup) => {
						return (
							<ActionListItem
								key={tileGroup.id}
								text={tileGroup.name}
								onClick={handleGroupClick(tileGroup.id)}
								rightIcon={
									tileGroup.type ===
									TileGroupType.Shared
										? "user-group"
										: undefined
								}
							/>
						);
					})
				}
			</Toolbar.SplitButton>
		)
	);
};

AddTilesButton.displayName = "AddTilesButton";
